import {
	LayoutDashboard,
	Radar,
	ClipboardList,
	Truck,
	Package,
	Boxes,
	Users,
	BarChart3,
	Store,
	Settings,
	Tag,
	Plug,
	Workflow,
} from 'lucide-react';

/**
 * Data-driven sidebar nav, grouped like Shopify admin's collapsible sections.
 * Each group renders as a collapsible header + NavLink list. `home` renders
 * as a single top-level link above the groups.
 *
 * Routes are wired to Placeholder pages until each phase in the development
 * plan builds the real view - see plan-an-admin-website-purrfect-sky.md.
 */
export const ROLES = ['PLATFORMADMIN', 'ADMIN', 'USER', 'VIEWER'];
export const ADMIN_ROLES = ['PLATFORMADMIN', 'ADMIN'];
export const USER_ROLES = ['PLATFORMADMIN', 'ADMIN', 'USER'];
export const PLATFORMADMIN_ROLES = ['PLATFORMADMIN'];

export const homeNavItem = { label: 'Home', to: '/', icon: LayoutDashboard, roles: ROLES };

export const navGroups = [
	// Flat top-level link (no `items`) -- renders via NavFlatLink instead of a
	// collapsible group, same as homeNavItem but living in navGroups so it
	// sits in the main nav list rather than needing its own hardcoded slot in
	// navigation.js.
	{ id: 'command-center', label: 'Today', icon: Radar, to: '/command-center', roles: ADMIN_ROLES },
	// Orders needing approval are a filter on this page now ("Needs approval",
	// /orders?view=approval) rather than their own nav item.
	{ id: 'orders', label: 'Orders', icon: ClipboardList, to: '/orders', roles: ROLES },
	{
		id: 'products',
		label: 'Products',
		icon: Package,
		items: [
			{ label: 'All Products', to: '/products', roles: ROLES },
			{ label: 'Kits', to: '/products/kits', roles: ROLES },
			{ label: 'Categories', to: '/products/categories', roles: USER_ROLES },
			// {
			// 	label: 'Shopify Mappings',
			// 	to: '/products/erp-mappings',
			// 	roles: PLATFORMADMIN_ROLES,
			// },
		],
	},
	{
		id: 'inventory',
		label: 'Inventory',
		icon: Boxes,
		items: [
			{ label: 'Inventory Levels', to: '/inventory', roles: USER_ROLES },
			{ label: 'Change Log', to: '/inventory/change-log', roles: PLATFORMADMIN_ROLES },
		],
	},
	{
		id: 'discounts',
		label: 'Discounts',
		icon: Tag,
		items: [
			{ label: 'Coupon Codes', to: '/coupons', roles: ADMIN_ROLES },
			// { label: 'Promotions', to: '/', roles: PLATFORMADMIN_ROLES },
		],
	},
	{
		id: 'customers',
		label: 'Registered Users',
		icon: Users,
		items: [
			{ label: 'All Registered Users', to: '/store-accounts', roles: ROLES },
			{ label: 'ERP Customers', to: '/customers', roles: ROLES },
		],
	},
	{
		id: 'fulfillments',
		label: 'Fulfillments',
		icon: Truck,
		items: [
			{ label: 'Shipped Orders', to: '/fulfillments', roles: ROLES },
			{
				label: 'Custom Fulfillment',
				to: '/products/fulfillment-routing',
				roles: ADMIN_ROLES,
			},
		],
	},	
	{
		id: 'workflows',
		label: 'Workflows',
		icon: Workflow,
		items: [
			{ label: 'All Workflows', to: '/workflows', roles: PLATFORMADMIN_ROLES },
			{ label: 'Runs', to: '/workflows/runs', roles: PLATFORMADMIN_ROLES },
			// Visible to the same audience as order approvals -- see
			// visibleGroupsForRole's `requiresApprover` filter in navigation.js.
			{ label: 'Approvals', to: '/workflows/approvals', roles: PLATFORMADMIN_ROLES, requiresApprover: true },
			{ label: 'Exceptions', to: '/workflows/exceptions', roles: PLATFORMADMIN_ROLES },
		],
	},
	{
		id: 'reports',
		label: 'Reports',
		icon: BarChart3,
		items: [
			{ label: 'Orders Report', to: '/reports/orders', roles: ROLES },
			{ label: 'Fulfillments Report', to: '/reports/fulfillments', roles: ROLES },
			{ label: 'Custom Fulfillment History', to: '/reports/custom-fulfillment', roles: ADMIN_ROLES },
			{ label: 'Inventory Report', to: '/reports/inventory', roles: ADMIN_ROLES },
			{ label: 'Loyalty Points Report', to: '/reports/loyalty', roles: ADMIN_ROLES },
			{ label: 'Coupon Usage Report', to: '/reports/coupon-usage', roles: ADMIN_ROLES },
			{
				label: 'Webhook Deliveries Report',
				to: '/reports/webhook-deliveries',
				roles: PLATFORMADMIN_ROLES,
			},
			{ label: 'ERP Sync Report', to: '/reports/sync-health', roles: PLATFORMADMIN_ROLES },
		],
	},
	{
		id: 'settings',
		label: 'Settings',
		icon: Store,
		items: [
			{ label: 'General', to: '/settings', roles: ADMIN_ROLES },
			{ label: 'Team', to: '/settings/users', roles: ADMIN_ROLES },
			{ label: 'Branding', to: '/settings/branding', roles: ADMIN_ROLES },
			{ label: 'Email', to: '/settings/email', roles: ADMIN_ROLES },
			{ label: 'Payment Methods', to: '/settings/payment-methods', roles: PLATFORMADMIN_ROLES },
			{ label: 'Shipping Methods', to: '/settings/shipping-methods', roles: PLATFORMADMIN_ROLES },
			{ label: 'FAQ', to: '/settings/faq', roles: ADMIN_ROLES },
			{ label: 'Holidays', to: '/settings/holidays', roles: PLATFORMADMIN_ROLES },
		],
	},
	{
		id: 'developer',
		label: 'Developer',
		icon: Plug,
		items: [
			{ label: 'API Tokens', to: '/settings/tokens', roles: PLATFORMADMIN_ROLES },
			{ label: 'Webhooks', to: '/settings/webhooks', roles: ADMIN_ROLES },
			{ label: 'ERP', to: '/settings/erp', roles: PLATFORMADMIN_ROLES },
			{ label: 'Iframe', to: '/settings/iframe', roles: PLATFORMADMIN_ROLES },
		],
	},
	{
		id: 'admin',
		label: 'Administration',
		icon: Settings,
		items: [
			{ label: 'Users & Permissions', to: '/admin/users', roles: PLATFORMADMIN_ROLES },
			{ label: 'ERP Credentials', to: '/admin/pace-account', roles: PLATFORMADMIN_ROLES },
			{ label: 'Audit Log', to: '/admin/audit-log', roles: PLATFORMADMIN_ROLES },
			{ label: 'Event Log', to: '/admin/event-log', roles: PLATFORMADMIN_ROLES },
		],
	},
];
